import { useEffect, useState } from "react";
import { ChevronDown, Loader2, ShieldQuestion } from "lucide-react";
import { toast } from "sonner";
import { api, fmtNumber } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// v55 — Bot Check. Display only; every value comes from the sheets.
// `bc === undefined` → feature off (render nothing); `bc === null` → not checked.
export const BOT_META = {
  high:   { word: "High risk",   short: "HIGH",    chip: "HIGH BOT RISK",   color: "#F43F5E", step: 4 },
  medium: { word: "Medium risk", short: "MEDIUM",  chip: "MEDIUM BOT RISK", color: "#FB923C", step: 3 },
  low:    { word: "Low risk",    short: "LOW",     chip: "LOW BOT RISK",    color: "#A1A1AA", step: 2 },
  none:   { word: "No sign",     short: "NO SIGN", chip: "NO BOT SIGN",     color: "#39FF14", step: 1 },
};
const STEPS = [["none", "No sign"], ["low", "Low"], ["medium", "Medium"], ["high", "High"]];

// v61 — one plain line per verdict; never how a verdict is worked out.
export const BOT_HELP = {
  none: "Nothing unusual found. The subscriber count looks like a real audience.",
  low: "Mostly fine. One or two small oddities in the numbers, but most of the audience looks real. Worth a quick look at recent videos before a big deal.",
  medium: "A significant part of the subscriber count looks inflated. Treat the number with caution and go by the likely real range shown.",
  high: "Most of the subscriber count is likely not real. Judge this creator by views and engagement, not by subscribers.",
  not_checked: "Not enough recent video data yet for a reliable check. This updates automatically.",
  small: "Small audience. Bot Check starts once this channel grows.",
};

// v64 — "not_checked" stays the internal value; only the visible name changed.
export const NOT_ENOUGH_META = { word: "Not enough data", short: "NO DATA", chip: "NOT ENOUGH DATA", color: "#71717A" };
// v68 — a blank Bot Check on a small channel (the server decides; no rule or number is ever sent).
export const SMALL_META = { word: "Small audience", short: "SMALL", chip: "SMALL AUDIENCE", color: "#71717A" };
const BLANK_META = { not_checked: NOT_ENOUGH_META, small: SMALL_META };
export const BOT_LEVELS = ["high", "medium", "low", "none", "small", "not_checked"].map((key) => ({
  key, ...(BOT_META[key] || BLANK_META[key]), help: BOT_HELP[key],
}));

// Hover on a computer, tap on a phone.
export function BotHelp({ verdict, testid, children }) {
  const [open, setOpen] = useState(false);
  const text = BOT_HELP[verdict];
  if (!text) return children;
  const hover = (v) => (e) => { if (e.pointerType === "mouse") setOpen(v); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span role="button" tabIndex={0} data-testid={testid} className="inline-flex max-w-full cursor-help outline-none focus-visible:ring-1 focus-visible:ring-zinc-500 rounded"
          onPointerEnter={hover(true)} onPointerLeave={hover(false)}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (e.nativeEvent.pointerType === "mouse") setOpen(true); else setOpen((o) => !o); }}>
          {children}
        </span>
      </PopoverTrigger>
      <PopoverContent side="top" onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={(e) => e.preventDefault()} onClick={(e) => e.stopPropagation()}
        data-testid={`${testid}-text`} className="w-64 p-2.5 text-xs leading-snug text-zinc-300 border hairline" style={{ background: "var(--surface-2)" }}>
        {text}
      </PopoverContent>
    </Popover>
  );
}

// v61 — any combination of the five; value is a comma string ("" = no filter).
export const BOT_RISK_OPTIONS = [
  { key: "high", label: "High" },
  { key: "medium", label: "Medium" },
  { key: "low", label: "Low" },
  { key: "none", label: "No sign" },
  { key: "not_checked", label: "Small audience / Not enough data" },
];
const LEGACY = { hide_high_medium: ["low", "none", "not_checked"], only_high_medium: ["high", "medium"], only_high: ["high"] };
export const normBotRisk = (v) => {
  const s = new Set((Array.isArray(v) ? v : String(v || "").split(",")).flatMap((k) => LEGACY[k] || [k]));
  return BOT_RISK_OPTIONS.filter((o) => s.has(o.key)).map((o) => o.key).join(",");
};
export const botRiskLabel = (v) => BOT_RISK_OPTIONS.filter((o) => normBotRisk(v).split(",").includes(o.key)).map((o) => o.label).join(" + ");

export const botSortRank = (bc) => (bc && BOT_META[bc.verdict] ? BOT_META[bc.verdict].step : 0);

const fmtX = (n) => `${Number.isInteger(n) ? n : Number(n.toFixed(1))}x`;
export const realRange = (bc) =>
  bc && bc.real_low != null && bc.real_high != null ? `${fmtNumber(bc.real_low)}–${fmtNumber(bc.real_high)}` : null;
export const inflatedText = (bc) => {
  if (!bc) return null;
  if (bc.inflated_low != null && bc.inflated_high != null) return `about ${fmtX(bc.inflated_low)} – ${fmtX(bc.inflated_high)}`;
  if (bc.inflated_about != null) return `about ${fmtX(bc.inflated_about)}`;
  return null;
};
const hasEstimate = (bc) => !!(realRange(bc) || inflatedText(bc));
const isFlagged = (bc) => bc && (bc.verdict === "high" || bc.verdict === "medium");
// v56 — Compare: a High/Medium creator never takes the Subscribers "best" highlight.
export const botNoBest = (r) => !!isFlagged(r.bot_check);

// v56 — My Radar: "Tier 1 · Medium bot risk", only the words in colour.
export function RadarBotRisk({ bc, testid }) {
  const m = bc && BOT_META[bc.verdict];
  if (!m) return null;
  const words = bc.verdict === "none" ? "No bot sign" : `${m.word.replace(" risk", "")} bot risk`;
  return <> · <BotHelp verdict={bc.verdict} testid={`${testid}-help`}><span data-testid={testid} style={{ color: m.color }}>{words}</span></BotHelp></>;
}

const chipStyle = (c) => ({ color: c, background: `${c}14`, border: `1px solid ${c}55` });

export function BotChip({ bc, testid }) {
  const m = bc && (BOT_META[bc.verdict] || BLANK_META[bc.verdict]);
  if (!m) return null;
  return (
    <BotHelp verdict={bc.verdict} testid={`${testid}-help`}>
      <span data-testid={testid} className="mono text-[9px] font-semibold tracking-wider px-1.5 py-0.5 rounded inline-block max-w-full leading-tight" style={chipStyle(m.color)}>
        {m.chip}
      </span>
    </BotHelp>
  );
}

export function BotHeaderChip({ bc, testid }) {
  if (!isFlagged(bc)) return null;
  const c = BOT_META[bc.verdict].color;
  return (
    <BotHelp verdict={bc.verdict} testid={`${testid}-help`}>
      <span data-testid={testid} className="mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-md border" style={{ color: c, borderColor: `${c}66`, background: `${c}14` }}>
        {BOT_META[bc.verdict].chip}
      </span>
    </BotHelp>
  );
}

export function BotRiskCell({ bc, testid }) {
  if (!bc || !BOT_META[bc.verdict]) return <BotHelp verdict="not_checked" testid={`${testid}-help`}><span data-testid={testid} className="mono text-[10px] text-zinc-600">—</span></BotHelp>;
  const m = BOT_META[bc.verdict];
  const rr = isFlagged(bc) ? realRange(bc) : null;
  return (
    <BotHelp verdict={bc.verdict} testid={`${testid}-help`}>
      <span data-testid={testid} className="inline-flex items-center gap-2 whitespace-nowrap">
        <span className="mono text-[10px] font-semibold px-1.5 py-0.5 rounded" style={chipStyle(m.color)}>{m.short}</span>
        {rr && <span className="mono text-[10px] text-zinc-500">real ~{rr}</span>}
      </span>
    </BotHelp>
  );
}

export function BotMeter({ verdict, testid, compact = false }) {
  const m = BOT_META[verdict];
  return (
    <div data-testid={testid} className={`grid grid-cols-4 gap-1 ${compact ? "w-40" : "w-full"}`}>
      {STEPS.map(([k, lbl]) => {
        const lit = m && k === verdict;
        return (
          <div key={k} className="min-w-0">
            <div className={`${compact ? "h-1.5" : "h-2"} rounded-sm`} style={{ background: lit ? m.color : "rgba(255,255,255,0.08)" }} />
            {!compact && (
              <div className="mono text-[9px] uppercase tracking-wider mt-1 truncate" style={{ color: lit ? m.color : "#71717A" }}>{lbl}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function notRealPct(bc, shown) {
  if (!bc || !shown || bc.real_low == null || bc.real_high == null || bc.real_high >= shown) return null;
  const hi = Math.min(99, Math.round((1 - bc.real_low / shown) * 100));
  const lo = Math.min(hi, Math.max(0, Math.round((1 - bc.real_high / shown) * 100)));
  return lo === hi ? `${lo}%` : `${lo}–${hi}%`;
}

// v62 — compact label | value rows so a flagged card isn't taller than the profile header.
function Box({ label, value, sub, color, testid }) {
  return (
    <div data-testid={testid} className="flex items-start justify-between gap-4 px-3 py-2 min-w-0 border-t hairline first:border-t-0">
      <div className="mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 pt-1 shrink-0">{label}</div>
      <div className="text-right min-w-0">
        <div className="mono text-sm font-semibold whitespace-nowrap" style={{ color }}>{value}</div>
        {sub && <div className="mono text-[10px] text-zinc-500 mt-0.5 leading-snug">{sub}</div>}
      </div>
    </div>
  );
}

export function BotCheckCard({ bc, shown, testid = "bot-check-card" }) {
  if (bc === undefined) return null;
  const m = bc && BOT_META[bc.verdict];
  const rr = m && realRange(bc);
  const it = m && inflatedText(bc);
  const pct = m && notRealPct(bc, shown);
  const est = m && isFlagged(bc) && hasEstimate(bc);
  const small = bc?.verdict === "small";
  return (
    <div data-testid={testid} data-verdict={m ? bc.verdict : small ? "small" : "not_checked"} className="border hairline rounded-md p-4 sm:p-5" style={{ background: "var(--surface)", ...(isFlagged(bc) ? { borderColor: `${m.color}66` } : {}) }}>
      <div className={`grid grid-cols-1 gap-y-4 gap-x-8 ${est ? "md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" : "md:grid-cols-[minmax(0,380px)_minmax(0,1fr)] md:items-end"}`}>
        <div className="min-w-0">
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Bot Check</div>
          {!m ? (
            <>
              <div data-testid={`${testid}-not-checked`} className="text-zinc-500 text-sm mt-2">{small ? "Bot Check: small audience" : "Bot Check: not enough data yet"}</div>
            </>
          ) : (
            <>
              <div className="mt-1 flex items-baseline gap-x-3 gap-y-0 flex-wrap">
                <span data-testid={`${testid}-verdict`} className="display text-2xl font-semibold" style={{ color: m.color }}>{m.word}</span>
                {isFlagged(bc) && <span className="text-xs text-zinc-400">subscriber count looks inflated</span>}
              </div>
              <div className="mt-3 max-w-md"><BotMeter verdict={bc.verdict} testid={`${testid}-meter`} /></div>
              {est && <div data-testid={`${testid}-helper`} className="text-xs text-zinc-400 mt-2.5 leading-snug max-w-2xl">{BOT_HELP[bc.verdict]}</div>}
            </>
          )}
        </div>
        {!est && <div data-testid={`${testid}-helper`} className="text-xs text-zinc-400 leading-snug max-w-2xl">{BOT_HELP[m ? bc.verdict : small ? "small" : "not_checked"]}</div>}
        {est && (
          <div className="rounded-md border hairline self-start" style={{ background: "var(--surface-2)" }}>
            {rr && <Box testid={`${testid}-real`} label="Likely real subscribers" value={rr.replace("–", " – ")} sub={`shown: ${fmtNumber(shown)}`} color="#E4E4E7" />}
            {it && <Box testid={`${testid}-inflated`} label="Count likely inflated by" value={it} sub={pct ? `about ${pct} of subscribers are likely not real` : null} color={m.color} />}
          </div>
        )}
      </div>
      <div className="mt-4 pt-3 border-t hairline flex flex-col md:flex-row md:justify-between gap-x-6 gap-y-1">
        <div className="mono text-[10px] text-zinc-500 leading-snug">Based on views, likes and comments against the subscriber count. A flag to check, not a verdict.</div>
        <div data-testid={`${testid}-updated`} className="mono text-[10px] text-zinc-600 leading-snug">Bot Check is updated regularly and a creator's status can change over time.</div>
      </div>
    </div>
  );
}

// v62 — header on top, Bot Check as a full-width strip under it: no card is ever
// stretched to the other's height, so nothing is left empty for any verdict.
export function BotTopRow({ on, card, children }) {
  if (!on) return children;
  return (
    <div data-testid="bot-top-row" className="flex flex-col gap-4 mb-6">
      {children}
      {card}
    </div>
  );
}

export function BotRiskFilter({ value, onChange, testid = "bot-risk-filter" }) {
  const sel = new Set(normBotRisk(value).split(",").filter(Boolean));
  const toggle = (k) => { const n = new Set(sel); if (n.has(k)) n.delete(k); else n.add(k); onChange(normBotRisk([...n])); };
  const btn = (on) => `px-2 py-0.5 text-[10px] mono uppercase tracking-wider rounded border hairline transition-colors duration-150 ${on ? "text-black" : "text-zinc-500 hover:text-white"}`;
  return (
    <div className="px-3 py-2 rounded-md border hairline text-sm" style={{ background: "var(--surface-2)" }} data-testid={testid}>
      <div className="text-zinc-400 text-xs uppercase tracking-wider mono mb-2 flex items-center gap-2">Bot risk <span className="normal-case tracking-normal text-zinc-600">· pick any</span></div>
      <div className="flex flex-wrap gap-1">
        {BOT_RISK_OPTIONS.map((o) => (
          <button key={o.key} type="button" aria-pressed={sel.has(o.key)} data-testid={`${testid}-${o.key}`} onClick={() => toggle(o.key)}
            className={btn(sel.has(o.key))} style={sel.has(o.key) ? { background: "var(--primary)" } : {}}>{o.label}</button>
        ))}
        {sel.size > 0 && <button type="button" data-testid={`${testid}-clear`} onClick={() => onChange("")} className={btn(false)}>Clear</button>}
      </div>
    </div>
  );
}

export function BotRiskActiveChip({ value, onClear, testid = "bot-risk-active-chip" }) {
  if (!value) return null;
  return (
    <button type="button" data-testid={testid} onClick={onClear}
      className="text-xs px-3 py-2 rounded-md border inline-flex items-center gap-2 border-cyan-400/70 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20 transition-colors duration-150"
      title="Clear the Bot risk filter">
      Bot risk: {botRiskLabel(value)} <span aria-hidden>×</span>
    </button>
  );
}

// v55 — the three Compare rows, placed right under Subscribers.
const NOT_ENOUGH = <span className="text-zinc-600">— not enough data</span>;
const SMALL_AUD = <span className="text-zinc-600">— small audience</span>;
const blankCell = (r) => (r.bot_check?.verdict === "small" ? SMALL_AUD : NOT_ENOUGH);
function CompareRow({ label, cells, testid }) {
  return (
    <div data-testid={testid} className="grid" style={{ gridTemplateColumns: `160px repeat(${cells.length}, minmax(0,1fr))`, borderBottom: "1px solid rgba(255,255,255,0.05)", background: "rgba(244,63,94,0.025)" }}>
      <div className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 px-4 py-3 border-r hairline">{label}</div>
      {cells.map((c, i) => <div key={i} data-testid={`${testid}-${i}`} className="px-4 py-3 border-r hairline display text-base min-w-0">{c}</div>)}
    </div>
  );
}

export function BotCompareRows({ rows }) {
  if (!rows.some((r) => r.bot_check !== undefined)) return null;
  const m = (r) => r.bot_check && BOT_META[r.bot_check.verdict];
  return (
    <>
      <CompareRow testid="compare-bot-risk-row" label="Bot risk" cells={rows.map((r) => (m(r) ? (
        <div><BotMeter verdict={r.bot_check.verdict} compact /><div className="mt-1.5"><BotHelp verdict={r.bot_check.verdict} testid={`compare-bot-word-help-${r.id}`}><span style={{ color: m(r).color }}>{m(r).word}</span></BotHelp></div></div>
      ) : <BotHelp verdict={r.bot_check?.verdict === "small" ? "small" : "not_checked"} testid={`compare-bot-not-checked-help-${r.id}`}>{blankCell(r)}</BotHelp>))} />
      <CompareRow testid="compare-bot-real-row" label="Likely real subscribers" cells={rows.map((r) => {
        if (!m(r)) return blankCell(r);
        if (r.bot_check.verdict === "none") return <span className="text-zinc-500">= shown</span>;
        const rr = realRange(r.bot_check);
        if (!rr) return isFlagged(r.bot_check) ? null : <span className="text-zinc-600">—</span>;
        return <span className="mono">{rr.replace("–", " – ")}</span>;
      })} />
      <CompareRow testid="compare-bot-inflated-row" label="Count likely inflated by" cells={rows.map((r) => {
        if (!m(r)) return blankCell(r);
        const it = r.bot_check.verdict === "none" ? null : inflatedText(r.bot_check);
        if (!it) return isFlagged(r.bot_check) ? null : <span className="text-zinc-600">—</span>;
        const pct = isFlagged(r.bot_check) ? notRealPct(r.bot_check, r.subscriber_count) : null;
        return (
          <div>
            <span className="mono">{it}</span>
            {pct && <div data-testid={`compare-bot-notreal-${r.id}`} className="mono text-[10px] text-zinc-500 mt-1">about {pct} of subscribers are likely not real</div>}
          </div>
        );
      })} />
    </>
  );
}

// v67 — Bot Check behind a click (clients). The verdict is fetched only on open,
// and every open is recorded as interest. Nothing about the verdict shows before.
export async function openBotCheck(ids, place) {
  const { data } = await api.post("/bot-check/open", { ids, place });
  return data;
}

export function BotRevealButton({ onOpen, label, testid, compact = false }) {
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setBusy(true);
    try { await onOpen(); } catch { toast.error("Could not open Bot Check. Try again."); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={go} disabled={busy} data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-md border hairline text-zinc-300 hover:text-white hover:border-white/30 transition-colors duration-150 disabled:opacity-60 ${compact ? "px-2 py-0.5 mono text-[10px] uppercase tracking-wider" : "px-3 py-1.5 text-xs"}`}>
      {busy ? <Loader2 size={compact ? 10 : 12} className="animate-spin" /> : <ShieldQuestion size={compact ? 10 : 12} />} {label}
    </button>
  );
}

export function BotLockedSection({ onOpen, testid = "bot-check-locked" }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await onOpen(); } catch { toast.error("Could not open Bot Check. Try again."); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={go} disabled={busy} data-testid={testid} aria-expanded={false}
      className="w-full border hairline rounded-md px-5 py-4 flex items-center justify-between gap-3 text-left hover:border-white/30 transition-colors duration-150"
      style={{ background: "var(--surface)" }}>
      <span className="flex items-center gap-3 min-w-0">
        <ShieldQuestion size={16} className="text-zinc-400 shrink-0" />
        <span className="min-w-0">
          <span className="block display text-base font-semibold text-white">Bot Check</span>
          <span className="block text-xs text-zinc-500 mt-0.5">Check this creator's bot risk</span>
        </span>
      </span>
      {busy ? <Loader2 size={16} className="animate-spin text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
    </button>
  );
}

// Tables (exchange page, Exchange Mapping): a per-creator "Check" button.
export function BotRiskReveal({ id, place, testid }) {
  const [bc, setBc] = useState(undefined);
  if (bc !== undefined) return <BotRiskCell bc={bc} testid={testid} />;
  const open = async () => { const d = await openBotCheck([id], place); setBc(d.items?.[id] ?? null); };
  return <BotRevealButton compact label="Check" testid={`${testid}-reveal`} onOpen={open} />;
}

// Compare: one row with a single "Reveal all" button.
export function BotRevealRow({ count, onOpen }) {
  return (
    <div data-testid="compare-bot-reveal-row" className="grid" style={{ gridTemplateColumns: "160px minmax(0,1fr)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
      <div className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 px-4 py-3 border-r hairline">Bot Check</div>
      <div className="px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <BotRevealButton onOpen={onOpen} testid="compare-bot-reveal-all" label={count === 1 ? "Reveal Bot Check" : `Reveal Bot Check for all ${count}`} />
        <span className="text-xs text-zinc-500">Check these creators' bot risk</span>
      </div>
    </div>
  );
}

// Profiles: { on, bc, card } — locked → a collapsed section until opened.
export function useProfileBot(src, id, place, testid = "bot-check-locked") {
  const locked = !!src?.bot_check_locked;
  const [got, setGot] = useState(undefined);
  useEffect(() => { setGot(undefined); }, [id]);
  const on = !!src && (locked || src.bot_check !== undefined);
  const bc = locked ? got : src?.bot_check;
  const open = async () => { const d = await openBotCheck([id], place); setGot(d.items?.[id] ?? null); };
  const card = locked && got === undefined
    ? <BotLockedSection onOpen={open} testid={testid} />
    : <BotCheckCard bc={bc} shown={src?.subscriber_count} />;
  return { on, bc: bc === undefined ? null : bc, card, locked, opened: !locked || got !== undefined };
}
